import { css } from '@emotion/react';
import {
  AnimatePresence, motion,
} from 'motion/react';
import {
  type FC,
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { itemWidth } from '~/_shared/constants/mainMenuUI.constants';
import { isPresentationalMapByPathname } from '~/_shared/presentationMode/presentationModeContext';
import { MainMenuContainer } from '~/mainMenu/mainMenu.container';
import { decideIsEnoughVerticalSpaceForMenuItems } from '~/mainMenu/mainMenu.helpers';
import {
  type MainMenuItem, MainMenuItems,
} from '~/mainMenu/mainMenuItem.enum';
import { MapSettingsContainer } from '~/map/settings/mapSettings.container';
import { useSlidingAnimationMsDuration } from '~/store/frontendState/graphicSettings/graphicSettings.selectors';
import { type SidebarApp } from './sidebarApp.enum';
import { type SidebarAppProps } from './sidebarAppComponentProps.type';
import { AccountSettingsAppComponent } from './sidebarApps/accountSettingsAppComponent';
import { MapToolsContainer } from './sidebarApps/mapTools.container';
import { MasterKeyToolContainer } from './sidebarApps/mapTools/masterKeyTool/masterKeyTool.container';
import { SaveMapViewContainer } from './sidebarApps/saveMapView/saveMapView.container';

export const SIDEBAR_WIDTH = 290;

const sidebarAppComponentMap: Partial<{ readonly [key in keyof typeof SidebarApp]: FC<SidebarAppProps> }> = {
  AccountSettings: AccountSettingsAppComponent,
  Boundary: MapToolsContainer,
  Filter: MapToolsContainer,
  Grouping: MapToolsContainer,
  HeatMap: MapToolsContainer,
  MapLegend: MapToolsContainer,
  MapSettings: MapSettingsContainer,
  MapTools: MapToolsContainer,
  MasterKey: MasterKeyToolContainer,
  Proximity: MapToolsContainer,
  Routing: MapToolsContainer,
  SaveMap: SaveMapViewContainer,
};

const sidebarAppMainMenuItemMap: { [key in keyof typeof SidebarApp]: MainMenuItem } = {
  AccountSettings: MainMenuItems.AccountSettings,
  Boundary: MainMenuItems.Boundary,
  Filter: MainMenuItems.Filter,
  Grouping: MainMenuItems.Grouping,
  HeatMap: MainMenuItems.HeatMap,
  MapLegend: MainMenuItems.MapLegend,
  MapSettings: MainMenuItems.MapSettings,
  MapTools: MainMenuItems.MapTools,
  MasterKey: MainMenuItems.MasterKey,
  Proximity: MainMenuItems.Proximity,
  Routing: MainMenuItems.Routing,
  SaveMap: MainMenuItems.SaveMapView,
  ShareMap: MainMenuItems.ShareMap,
};

const getAppFromMainMenuItem = (item: MainMenuItem): SidebarApp | undefined =>
  Object.entries(sidebarAppMainMenuItemMap).find(([, i]) => i === item)?.[0] as SidebarApp;

const wrapperStyle = ({ isOnTopOfMap }: { isOnTopOfMap: boolean }) => css({
  position: isOnTopOfMap ? 'absolute' : 'static',
  display: 'flex',
  height: '100%',
});

const menuStyle = ({ isSidebarVisible }: { isSidebarVisible: boolean }) => css({
  height: '100%',
  position: 'relative',
  overflow: isSidebarVisible ? 'visible' : 'hidden',
  width: isSidebarVisible ? itemWidth : 0,
});

const appWrapperStyle = css({
  overflow: 'clip',
  willChange: 'width',
});

type SidebarProps = Readonly<{
  isMobileScreen: boolean;
  isSidebarVisible: boolean;
  noMainMenu?: boolean;
  openedApp: SidebarApp | null;
  pathname: string;
  sidebarHeight: number;
  visibleMenuItems: ReadonlySet<MainMenuItem>;

  close?: () => void;
  openApp: (app: SidebarApp) => void;
}>;

export const SidebarComponent = (props: SidebarProps) => {
  const slidingAnimationDuration = useSlidingAnimationMsDuration();
  const duration = slidingAnimationDuration / 1000; // motion uses seconds

  const [isAppVisible, setIsAppVisible] = useState(false);

  const AppComponent = useMemo(() => props.openedApp && sidebarAppComponentMap[props.openedApp], [props.openedApp]);

  const isSidebarOnTopOfMap = useMemo(() => {
    if (!isPresentationalMapByPathname(props.pathname)) {
      return false;
    }
    return !decideIsEnoughVerticalSpaceForMenuItems(props.sidebarHeight, props.visibleMenuItems.size);
  }, [props.pathname, props.sidebarHeight, props.visibleMenuItems]);

  const openApp = useCallback((item: MainMenuItem) => {
    const app = getAppFromMainMenuItem(item);
    return app && props.openApp(app);
  }, [props]);

  const triggerClose = useCallback(() => {
    setIsAppVisible(false);
  }, []);

  useEffect(() => {
    if (props.openedApp && sidebarAppComponentMap[props.openedApp]) {
      setIsAppVisible(true);
    }
    else {
      setIsAppVisible(false);
    }
  }, [props.openedApp]);

  return (
    <div css={wrapperStyle({ isOnTopOfMap: isSidebarOnTopOfMap })}>
      {!props.noMainMenu && !props.isMobileScreen && (
        <div css={menuStyle({ isSidebarVisible: props.isSidebarVisible })}>
          <MainMenuContainer
            visibleMenuItems={props.visibleMenuItems}
            menuHeight={props.sidebarHeight}
            onItemClick={openApp}
            selectedItem={props.openedApp && sidebarAppMainMenuItemMap[props.openedApp]}
          />
        </div>
      )}

      <AnimatePresence onExitComplete={props.close}>
        {isAppVisible && AppComponent && (
          <motion.div
            key="sidebar-app"
            css={appWrapperStyle}
            initial={{ width: 0 }}
            animate={{ width: SIDEBAR_WIDTH }}
            exit={{ width: 0 }}
            transition={{ duration, ease: 'easeInOut' }}
          >
            <div css={{ width: SIDEBAR_WIDTH }}>
              <AppComponent onClose={triggerClose} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
